// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// usePageContext (T-502) — publishes the assistant's "where am I?" context.
//
// Route-derived only: it reads the active facility (useFacility), the current
// route + params (useLocation + matchPath), and the active module
// (useAppNavigation) and returns a small, stable descriptor the FloatingAssistant
// uses to (a) default the capture workflow and (b) let converse reference
// "this strain / room / crop" by injecting the focused record into the prompt.
//
// Pages with no recognizable record still get useful context (facility + module).

import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useFacility } from "@/contexts/FacilityContext";
import { useAppNavigation } from "@/hooks/useAppNavigation";
import type { WorkflowType } from "@/components/hands-free/types";
import type { SimilarSourceTable } from "@/hooks/prometheus-agent/useSimilar";

export interface PageRecord {
  /** Human-facing noun for "this ___" (e.g. "crop", "breeding project"). */
  type: string;
  /** The focused record id pulled from the route. */
  id: string;
  /** Underlying table, when known (used for prompt grounding). */
  table?: string;
  /** When set, a find-similar action is available for this record. */
  similarTable?: SimilarSourceTable;
}

export interface PageContext {
  facilityId: string | null;
  facilityName: string | null;
  /** Coarse module bucket, e.g. "arca" | "abacus" | "animus" | "aurum" | "atlas". */
  module: string | null;
  /** Friendly module label from the nav config, when resolvable. */
  moduleLabel: string | null;
  pathname: string;
  record: PageRecord | null;
  /** Default capture workflow for this page, when the route implies one. */
  captureWorkflow?: WorkflowType;
}

// ── Route → record / capture-workflow rules (most specific first) ─────────────

interface RouteRule {
  path: string;
  record?: (params: Record<string, string | undefined>) => PageRecord | null;
  captureWorkflow?: WorkflowType;
}

const ROUTE_RULES: RouteRule[] = [
  {
    path: "/crops/:id",
    captureWorkflow: "crop_note",
    record: (p) => (p.id ? { type: "crop", id: p.id, table: "crops" } : null),
  },
  { path: "/arca/runofflogger", captureWorkflow: "runoff_log" },
  { path: "/arca/application-log", captureWorkflow: "application_log" },
  { path: "/abacus/logger", captureWorkflow: "yield_batch" },
  {
    path: "/abacus/quality/batch/:batchId",
    record: (p) => (p.batchId ? { type: "batch", id: p.batchId, table: "yield_batches" } : null),
  },
  {
    path: "/animus/origins/:projectId/selections",
    record: (p) =>
      p.projectId ? { type: "breeding project", id: p.projectId, table: "breeding_projects" } : null,
  },
  {
    path: "/animus/origins/:projectId",
    record: (p) =>
      p.projectId ? { type: "breeding project", id: p.projectId, table: "breeding_projects" } : null,
  },
  {
    path: "/animus/traits/:projectId",
    record: (p) =>
      p.projectId ? { type: "trait project", id: p.projectId, table: "trait_projects" } : null,
  },
];

function moduleFromPath(pathname: string): string | null {
  const seg = pathname.split("/").filter(Boolean)[0];
  return seg ?? null;
}

export function usePageContext(): PageContext {
  const { activeFacility } = useFacility();
  const { activeModule } = useAppNavigation();
  const location = useLocation();
  const pathname = location.pathname;

  return useMemo<PageContext>(() => {
    let record: PageRecord | null = null;
    let captureWorkflow: WorkflowType | undefined;

    for (const rule of ROUTE_RULES) {
      const match = matchPath({ path: rule.path, end: false }, pathname);
      if (!match) continue;
      if (rule.captureWorkflow && !captureWorkflow) captureWorkflow = rule.captureWorkflow;
      if (rule.record && !record) record = rule.record(match.params);
      // Keep scanning only if we still lack a record but already have a workflow,
      // so a less-specific workflow rule can't override a specific record match.
      if (record) break;
    }

    return {
      facilityId: activeFacility?.id ?? null,
      facilityName: activeFacility?.name ?? null,
      module: moduleFromPath(pathname),
      moduleLabel: activeModule?.label ?? null,
      pathname,
      record,
      captureWorkflow,
    };
  }, [activeFacility?.id, activeFacility?.name, activeModule?.label, pathname]);
}

/** Builds a short grounding preamble for converse prompts when a record is focused. */
export function buildContextPreamble(ctx: PageContext): string | null {
  if (!ctx.record) return null;
  const { type, id } = ctx.record;
  return `(Context: the user is currently viewing ${type} ${id}. When they say "this ${type}", resolve it to that record.)`;
}
